import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
import { randomStr } from '../../Shared';
const componentName = 'Toast';
const prefixName = `ui-${componentName}`;
export const commonOffsetSize = 20;
export const toastClasses = {
    container: `${prefixName}-container-${randomStr()}`,
    component: `${prefixName}-component-${randomStr()}`,
    icon: `${prefixName}-icon`,
    iconWrapper: `${prefixName}-icon-wrapper`,
    iconInfo: `${prefixName}-icon-info`,
    iconSuccess: `${prefixName}-icon-success`,
    iconWarn: `${prefixName}-icon-warn`,
    iconError: `${prefixName}-icon-error`,
    main: `${prefixName}-main`,
    title: `${prefixName}-title`,
    content: `${prefixName}-content`,
    close: `${prefixName}-close`,
    action: `${prefixName}-action`,
    list: `${prefixName}-list-${randomStr()}`,
    top: `${prefixName}-top`,
    topStart: `${prefixName}-top-start`,
    topEnd: `${prefixName}-top-end`,
    bottom: `${prefixName}-bottom`,
    bottomStart: `${prefixName}-bottom-start`,
    bottomEnd: `${prefixName}-bottom-end`,
    scroll: `${prefixName}-scroll`,
    item: `${prefixName}-item`,
    screenReadOnly: `${prefixName}-screenReadOnly`,
};
export function getToastComponentStyle(params) {
    const { theme, screenReadOnly } = params;
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const styles = {
        [`&.${toastClasses.component}`]: {
            fontFamily: tk(token.fontFamily),
            display: 'flex',
            boxSizing: 'border-box',
            minHeight: tk(token.minHeight),
            maxWidth: tk(token.maxWidth),
            padding: '16px',
            paddingLeft: token.minHeight,
            paddingRight: '21px',
            border: `0.5px solid ${tk(token.borderColor)}`,
            borderRadius: '8px',
            boxShadow: tk(token.boxShadow),
            background: tk(token.background),
            position: 'relative',
            flexDirection: 'column',
            ...(screenReadOnly
                ? {
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    borderWidth: 0,
                }
                : {}),
            [`.${toastClasses.iconWrapper}`]: {
                position: 'absolute',
                top: 0,
                left: 0,
                width: tk(token.minHeight),
                height: tk(token.minHeight),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginInlineEnd: '8px',
            },
            [`.${toastClasses.icon}`]: {
                fontSize: '20px',
                height: '20px',
                [`&.${toastClasses.iconInfo}`]: {
                    color: tk(token.iconColor.info),
                },
                [`&.${toastClasses.iconSuccess}`]: {
                    color: tk(token.iconColor.success),
                },
                [`&.${toastClasses.iconWarn}`]: {
                    color: tk(token.iconColor.warn),
                },
                [`&.${toastClasses.iconError}`]: {
                    color: tk(token.iconColor.error),
                },
            },
            [`.${toastClasses.main}`]: {
                display: 'flex',
                flexDirection: 'column',
                marginInlineStart: '-8px',
            },
            [`.${toastClasses.title}`]: {
                fontSize: '14px',
                color: tk(token.color),
                lineHeight: '20px',
                fontWeight: '500',
                textAlign: 'left',
                boxSizing: 'border-box',
                paddingRight: '24px',
                marginBottom: '4px',
            },
            [`.${toastClasses.content}`]: {
                fontSize: '14px',
                color: tk(token.color),
                lineHeight: '20px',
                margin: 0,
                paddingRight: '24px',
                fontWeight: '400',
                textAlign: 'left',
                boxSizing: 'border-box',
                a: {
                    color: tk(token.linkColor),
                },
            },
            [`.${toastClasses.close}`]: {
                position: 'absolute',
                right: '10px',
                top: '10px',
            },
            [`.${toastClasses.action}`]: {
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '16px',
                '>button': {
                    marginInlineEnd: '8px',
                    '&:last-child': {
                        marginInlineEnd: 0,
                    },
                },
            },
        },
    };
    return styles;
}
export function getToastListStyle(params) {
    const { theme, hasContainer } = params;
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const { zIndex } = params.config;
    const listStyles = {
        zIndex: zIndex ?? tk(token.zIndex),
        maxHeight: '100%',
        overflow: 'visible',
        boxSizing: 'border-box',
        ...(hasContainer
            ? {
                padding: `${commonOffsetSize}px`,
            }
            : {
                paddingTop: 0,
                position: 'fixed',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
            }),
        [`&.${toastClasses.scroll}`]: {
            [`&.${toastClasses.top},&.${toastClasses.topStart},&.${toastClasses.topEnd},&.${toastClasses.bottom},&.${toastClasses.bottomStart},&.${toastClasses.bottomEnd}`]: {
                top: 0,
                overflow: 'auto',
                padding: `${commonOffsetSize}px`,
            },
        },
        [`>.${toastClasses.item}`]: {
            position: 'initial',
            paddingBottom: `${commonOffsetSize}px`,
            boxSizing: 'border-box',
            '&:first-of-type': {
                paddingTop: '0',
            },
            '&:last-of-type': {
                paddingBottom: '0',
            },
            [`&.${toastClasses.screenReadOnly}`]: {
                position: 'fixed',
                left: '-99999px',
                top: '-99999px',
                opacity: '0',
                padding: '0',
            },
        },
        [`&.${toastClasses.top}`]: {
            top: `${commonOffsetSize}px`,
        },
        [`&.${toastClasses.topStart}`]: {
            top: `${commonOffsetSize}px`,
            left: `${commonOffsetSize}px`,
            right: 'unset',
            transform: 'translateX(0)',
        },
        [`&.${toastClasses.topEnd}`]: {
            top: `${commonOffsetSize}px`,
            left: 'unset',
            right: `${commonOffsetSize}px`,
            transform: 'translateX(0)',
        },
        [`&.${toastClasses.bottom}`]: {
            top: 'unset',
            bottom: `${commonOffsetSize}px`,
        },
        [`&.${toastClasses.bottomStart}`]: {
            top: 'unset',
            bottom: `${commonOffsetSize}px`,
            right: 'unset',
            left: `${commonOffsetSize}px`,
            transform: 'translateX(0)',
        },
        [`&.${toastClasses.bottomEnd}`]: {
            top: 'unset',
            bottom: `${commonOffsetSize}px`,
            right: `${commonOffsetSize}px`,
            left: 'unset',
            transform: 'translateX(0)',
        },
    };
    return listStyles;
}
